import styled from '@emotion/styled'
import { Dropdown } from '@leshen/ui'
import { graphql, navigate, withPrefix } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import React from 'react'
import SeoCtaBar from '../components/SeoCtaBar'
import footer from '../components/Footer'
import header from '../components/Header'
import { metadata } from '../data/metadata'
import { Done, Phone } from 'emotion-icons/material'
import { Layout, Head as HeadComponent } from '@leshen/gatsby-theme-sylex'
import PhoneCTA from '@leshen/gatsby-theme-leshen/src/components/PhoneCTA'
import siteTheme from 'gatsby-theme-dish/theme'
import { getCityFAQ, getCityFAQSchema } from '../data/city'
import { faqsToSchema, faqToItem } from '../utils/faq'
import { createPageData } from '../utils/page'

import {
  Main,
  Accordion,
  AvailabilityForm,
  Billboard,
  Brandy,
  Column,
  Footer,
  Header,
  Columns,
  LinkButton,
  List,
  ListItem,
  PackageCard,
  SplitContent,
  Stack,
  Typography,
  useMapi,
  VariableContent,
} from '@leshen/gatsby-theme-leshen'

export function Head({ data }) {
  const node = data.pageInfo.edges[0].node
  return (
    <>
      <HeadComponent
        pageContext={{ page: createPageData(node) }}
        data={{ site: { siteMetadata: metadata } }}
      />
      <script type="application/ld+json">
        {faqsToSchema([
          getCityFAQSchema(node.FAQ_1, node.State, node.City),
          getCityFAQSchema(node.FAQ_2, node.State, node.City),
          getCityFAQSchema(node.FAQ_3, node.State, node.City),
          getCityFAQSchema(node.FAQ_4, node.State, node.City),
          getCityFAQSchema(node.FAQ_5, node.State, node.City),
          getCityFAQSchema(node.FAQ_6, node.State, node.City),
        ])}
      </script>
    </>
  )
}

function City({ data }) {
  const { rotatedNumber } = useMapi()
  const node = data.pageInfo.edges[0].node
  const {
    State,
    City,
    FAQ_1,
    FAQ_2,
    FAQ_3,
    FAQ_4,
    FAQ_5,
    FAQ_6,
    Customer_Service,
  } = node

  const cityDropdownData = data.cities.edges.map((current) => ({
    label: current.node.City,
    value: current.node.Page_Path,
  }))

  return (
    <Layout
      page={createPageData(node)}
      siteMetadata={metadata}
      header={<Header {...header()} />}
      footer={<Footer {...footer()} />}
      main={
        <Main>
          <Billboard
            backgroundColor="midgray"
            variant="split"
            image={
              <StaticImage
                loading="eager"
                src="../images/lifestyle/family-watching-show.webp"
                alt="Family watching tv"
                placeholder="blurred"
              />
            }
            mobileImage={
              <StaticImage
                loading="eager"
                src="../images/lifestyle/family-watching-show.webp"
                alt="Family watching tv"
                placeholder="blurred"
              />
            }
            mainContent={
              <>
                <Typography variant="h1">
                  DISH Network in {City}, {State}
                </Typography>
                <Typography variant="h4">
                  <strong>Find DISH Satellite TV near your area today!</strong>
                </Typography>
                <List>
                  <ListItem icon={<Done />}>
                    <Typography>Local channels</Typography>
                  </ListItem>
                  <ListItem icon={<Done />}>
                    <Typography>Sports packages</Typography>
                  </ListItem>
                  <ListItem icon={<Done />}>
                    <Typography>Endless HD entertainment</Typography>
                  </ListItem>
                </List>
                <br />
                <PhoneCTA color="primary">Call to Learn More!</PhoneCTA>
              </>
            }
          />

          <SeoCtaBar />

          <VariableContent
            style={{ backgroundColor: siteTheme.colors.light }}
            mainContent={
              <>
                <Typography variant="h2">
                  DISH SATELLITE TV PACKAGES IN YOUR CITY
                </Typography>
                <Typography variant="body">
                  Find the best DISH packages in {City}, {State} featuring the
                  channels you want. And with a 2-year price guarantee, you’ll
                  never pay more than expected for 24 months.
                </Typography>
              </>
            }
          >
            <Stack spacing="xxl">
              <Columns>
                {data?.allBrandyPackage?.edges?.map(({ node }) => (
                  <PackageCard
                    key={node.brandy_id}
                    packageData={node}
                    content={<PhoneCTA color="primary">Call</PhoneCTA>}
                  />
                ))}
              </Columns>
              <Brandy
                text={data.disclaimerTwo.text}
                variant="legal"
                style={{
                  textAlign: 'center',
                }}
              />
              <Brandy
                text={data.disclaimerThree.text}
                variant="legal"
                style={{
                  textAlign: 'center',
                  margin: 'auto',
                }}
              />
            </Stack>
          </VariableContent>

          <VariableContent
            mainContent={
              <>
                <Typography variant="h2" className="uppercase">
                  MOVING TO THE {City} AREA?{' '}
                </Typography>
                <Typography variant="body">
                  DISH is ready to dive in and help you get settled into your
                  new home with the right TV package.
                </Typography>
              </>
            }
          >
            <Stack>
              <Columns
                className="zip-form-box center-text info-cards"
                leftAligned={false}
              >
                <Column
                  style={{ paddingTop: '20px' }}
                  image={
                    <StaticImage
                      style={{ marginBottom: '16px' }}
                      src="../images/icons/tv-remote.svg"
                      alt="tv remote"
                      placeholder="blurred"
                    />
                  }
                >
                  <Typography variant="h4">
                    <strong>Existing DISH customer?</strong>
                  </Typography>
                  <Typography className="center-text" variant="body">
                    We’ll help you move your existing services to your new place
                    in no time.
                  </Typography>
                  <LinkButton
                    to={`tel:${rotatedNumber}`}
                    target="_blank"
                    color="primary"
                  >
                    Contact us
                  </LinkButton>
                </Column>
                <Column
                  style={{ paddingTop: '20px' }}
                  image={
                    <StaticImage
                      style={{ marginBottom: '16px' }}
                      src="../images/icons/hands-raised.svg"
                      alt="hands raised"
                      placeholder="blurred"
                    />
                  }
                >
                  <Typography variant="h4">
                    <strong>Ready to become a DISH customer?</strong>
                  </Typography>
                  <Typography className="center-text" variant="body">
                    Welcome to the family—now let’s see what’s available in your
                    area first!
                  </Typography>
                  <AvailabilityForm
                    buttonColor="primary"
                    buttonText="Find"
                    placeholderText="Zip code"
                  />
                </Column>
              </Columns>
            </Stack>
          </VariableContent>

          <SplitContent
            backgroundColor="light"
            image={
              <StaticImage
                src="../images/lifestyle/party.webp"
                alt="people laughing at show together"
                placeholder="blurred"
              />
            }
            mainContent={
              <>
                <Typography variant="h2">
                  LOCAL CHANNELS MAKE DISH YOUR NEIGHBORHOOD WATCH
                </Typography>
                <Typography variant="body">
                  All DISH packages in {City} come with local channels so you
                  can keep up with your local sports team or your favorite news
                  channel. You’ll also have access to three months FREE of
                  premium channels like Showtime and the DISH Movie Pack.*
                </Typography>
                <Brandy
                  text={data.disclaimerFour.text}
                  variant="legal"
                  style={{
                    textAlign: 'center',
                  }}
                />
              </>
            }
          />

          <VariableContent
            mainContent={
              <>
                <Typography variant="h2">
                  Why Get DISH Network Near Me?
                </Typography>
              </>
            }
          >
            <Stack>
              <Columns className="center-text info-cards">
                <Column
                  style={{ paddingTop: '20px' }}
                  image={
                    <StaticImage
                      src="../images/icons/bill.svg"
                      alt=""
                      placeholder="blurred"
                    />
                  }
                >
                  <Typography className="center-text" variant="h4">
                    <strong>2-year TV price guarantee</strong>
                  </Typography>
                  <Typography className="center-text" variant="body">
                    The only provider that guarantees your monthly TV price for
                    two years
                  </Typography>
                </Column>
                <Column
                  style={{ paddingTop: '20px' }}
                  image={
                    <StaticImage
                      src="../images/icons/van.svg"
                      alt=""
                      placeholder="blurred"
                    />
                  }
                >
                  <Typography className="center-text" variant="h4">
                    <strong>Free installation</strong>
                  </Typography>
                  <Typography className="center-text" variant="body">
                    Kick back and let the pros handle your DISH Network
                    installation.
                  </Typography>
                </Column>
                <Column
                  style={{ paddingTop: '20px' }}
                  image={
                    <StaticImage
                      src="../images/icons/shield.svg"
                      alt=""
                      placeholder="blurred"
                    />
                  }
                >
                  <Typography className="center-text" variant="h4">
                    <strong>Signal Reliability Guarantee</strong>
                  </Typography>
                  <Typography className="center-text" variant="body">
                    A reliable signal or your money back.
                  </Typography>
                </Column>
              </Columns>
            </Stack>
          </VariableContent>

          <SplitContent
            backgroundColor="light"
            image={
              <StaticImage
                src="../images/lifestyle/games-dvr.webp"
                alt="dvr and tv"
                placeholder="blurred"
              />
            }
            mainContent={
              <>
                <Typography variant="h2">
                  SCORE BIG WITH DISH SPORTS PACKAGES
                </Typography>
                <Typography variant="h5">
                  Catch every home run, slam dunk, and touchdown with a DISH
                  sports package. From college sports to professional leagues,
                  DISH sports packages get you in the game without ever leaving
                  your couch.
                </Typography>
                <PhoneCTA color="primary">Call to order!</PhoneCTA>
              </>
            }
          />

          <SplitContent
            image={
              <StaticImage
                src="../images/lifestyle/family-time.webp"
                alt="Devices"
                placeholder="blurred"
              />
            }
            mainContent={
              <>
                <Typography variant="h2" className="uppercase">
                  BRING INTERNET AND TV TOGETHER IN {City}
                </Typography>
                <Typography variant="body">
                  DISH does TV, but we also work with internet providers to
                  bring the complete entertainment experience into your home in{' '}
                  {City}. Maximize your deals and save big when you bundle your
                  TV with internet partners like Hughesnet and Frontier.
                </Typography>
              </>
            }
          />

          <VariableContent
            backgroundColor="dark"
            mainContent={
              <>
                <Typography variant="h2">
                  Looking for DISH in {State}?
                </Typography>
                <Dropdown
                  options={cityDropdownData}
                  onChange={(e) => navigate(e.value)}
                  placeholderText="Select City"
                  aoa=""
                />
                {cityDropdownData?.map((current) => (
                  <StyledLink
                    href={withPrefix(current.value)}
                    key={current.value}
                  >
                    {current.label}
                  </StyledLink>
                ))}
              </>
            }
          ></VariableContent>

          {/* only show based on dsm entry of Customer_Service being Yes (CA, FL, GA, and TX) */}
          {Customer_Service && (
            <VariableContent
              mainContent={
                <>
                  <Typography variant="h2">
                    DISH Network Customer Service Numbers & Hours
                  </Typography>
                  <Typography variant="body">
                    Call DISH now to place an order or get DISH Network TV
                    support.
                  </Typography>
                </>
              }
              alignMainContent="Center"
              alignImageToBottom={false}
              centeredContent={false}
              backgroundColor={null}
              breadcrumbProps={null}
              customLink={null}
            >
              <Stack>
                <Columns leftAligned={false} className="center-text info-cards">
                  <Column
                    style={{ paddingTop: '20px' }}
                    image={
                      <StaticImage
                        style={{ marginBottom: '16px' }}
                        src="../images/icons/customer-service.svg"
                        alt="customer service agent icon"
                        placeholder="blurred"
                      />
                    }
                  >
                    <Typography className="center-text" variant="h4">
                      <strong>New Orders</strong>
                    </Typography>
                    <Typography className="center-text" variant="body">
                      6am–8pm MT, M–F
                    </Typography>
                    <Typography className="center-text" variant="body">
                      Español 7am–7pm MT, M–F
                    </Typography>
                    <PhoneCTA color="primary" />
                  </Column>
                  <Column
                    style={{ paddingTop: '20px' }}
                    image={
                      <StaticImage
                        style={{ marginBottom: '16px' }}
                        src="../images/icons/red-van.svg"
                        alt="red van icon"
                        placeholder="blurred"
                      />
                    }
                  >
                    <Typography className="center-text" variant="h4">
                      <strong>Customer Care</strong>
                    </Typography>
                    <Typography className="center-text" variant="body">
                      Technical support: available 24/7
                    </Typography>
                    <Typography className="center-text" variant="body">
                      Account help: 6am–10pm MT daily
                    </Typography>
                    <LinkButton
                      to="tel:8003333474"
                      target="_blank"
                      color="primary"
                      leftIcon={<Phone />}
                    >
                      1-800-333-3474
                    </LinkButton>
                  </Column>
                </Columns>
              </Stack>
            </VariableContent>
          )}

          <VariableContent backgroundColor="light">
            <Typography variant="h2">Frequently Asked Questions</Typography>
            <Accordion
              items={[
                faqToItem(getCityFAQ(FAQ_1, State, City)),
                faqToItem(getCityFAQ(FAQ_2, State, City)),
                faqToItem(getCityFAQ(FAQ_3, State, City)),
                faqToItem(getCityFAQ(FAQ_4, State, City)),
                faqToItem(getCityFAQ(FAQ_5, State, City)),
                faqToItem(getCityFAQ(FAQ_6, State, City)),
              ]}
            />{' '}
          </VariableContent>
        </Main>
      }
    />
  )
}

const StyledLink = styled.a`
  display: none !important;
`
export default City

export const query = graphql`
  query CityPageQuery($pagePath: String, $stateAbbreviation: String) {
    pageInfo: allDatasetManagerDishAoa(
      filter: { Page_Path: { eq: $pagePath } }
    ) {
      edges {
        node {
          City
          FAQ_1
          FAQ_2
          FAQ_3
          FAQ_4
          FAQ_5
          FAQ_6
          Page_Path
          State
          Region
          Meta_Description
          Meta_Title
          Promo_Code
          Customer_Service
        }
      }
    }
    cities: allDatasetManagerDishAoa(
      filter: { State: { eq: $stateAbbreviation }, Area_Type: { eq: "city" } }
    ) {
      edges {
        node {
          City
          Page_Path
        }
      }
    }
    allBrandyPackage(
      sort: { name: ASC }
      filter: {
        brandy_id: {
          in: ["dn-americastop120", "dn-americastop200", "dn-americastop250"]
        }
      }
    ) {
      edges {
        node {
          brandy_id
          bullets {
            text
          }
          price {
            amount
            duration
          }
          brand_token
          name
          id
        }
      }
    }
    disclaimerTwo: brandyDisclaimer(brandy_id: { eq: "disclaimer-dnfooter1" }) {
      id
      text
    }
    disclaimerThree: brandyDisclaimer(
      brandy_id: { eq: "disclaimer-googleasst" }
    ) {
      brandy_id
      text
    }
    disclaimerFour: brandyDisclaimer(brandy_id: { eq: "disclaimer-premium" }) {
      brandy_id
      text
    }
  }
`
